type QueryValue = string | (string | null)[] | null | undefined

export interface MvtRoute {
  path: string
  params: Record<string, string>
  query: Record<string, QueryValue>
  hash: string
  hashQuery: Record<string, string>
}

export interface NavigateLocation {
  path?: string
  query?: Record<string, string>
  hash?: string
}

export interface NavigateOptions {
  replace?: boolean
}

export type RouterLocation = Pick<URL, "pathname" | "search" | "hash">

export type RouteWatcherCallbacks = {
  onEnter?: () => void
  onLeave?: () => void
}

export type RouteWatcher = { readonly isPresent: boolean }

export type Router = {
  readonly route: MvtRoute
  navigate: (to: string | NavigateLocation, options?: NavigateOptions) => Promise<void>
  watch: (target: string, callbacks?: RouteWatcherCallbacks) => RouteWatcher
  goBack: () => void
}

// A hash carries its own query, as #auth/login?email=…, which the route keeps apart.
export const hashOf = (urlHash: string) => {
  const entry = urlHash.replace(/^#/, "")
  const queryAt = entry.indexOf("?")

  return {
    hash: entry && `#${queryAt < 0 ? entry : entry.slice(0, queryAt)}`,
    hashQuery: queryAt < 0 ? {} : Object.fromEntries(new URLSearchParams(entry.slice(queryAt + 1))),
  }
}

// The route is the URL, parsed; on Vue 3 the path has no params to fill.
export const parseLocationToRoute = ({ pathname, search, hash }: RouterLocation): MvtRoute => ({
  path: pathname,
  params: {},
  query: Object.fromEntries(new URLSearchParams(search)),
  ...hashOf(hash),
})

export const routeMatcher = (written: string) => {
  if (!/^[#/]/.test(written)) {
    throw new Error(`$mvt.router.watch takes a hash starting with "#" or a path starting with "/": ${written}`)
  }

  if (written.startsWith("#") && written.includes("?")) {
    throw new Error(`$mvt.router.watch takes a hash without its query: ${written}`)
  }

  const target = written.length > 1 ? written.replace(/\/$/, "") : written

  return (route: MvtRoute) => {
    const current = target.startsWith("#") ? route.hash : route.path

    return current === target || current.startsWith(`${target}/`)
  }
}
